import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  InputBase,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddTaskIcon from "@mui/icons-material/AddTask";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import BusinessIcon from "@mui/icons-material/Business";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EventIcon from "@mui/icons-material/Event";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SearchIcon from "@mui/icons-material/Search";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useLocation, useNavigate } from "react-router-dom";
import ConfirmDeleteDialog from "../../../common/confirmDeleteAlert";
import Loader from "../../../common/loader";
import {
  deleteTask,
  getTasks,
  Task,
  TaskStatus,
} from "../../../services/task.service";
import TaskFormDialog from "./TaskFormDialog";

const formatName = (person: any) =>
  [person?.firstName, person?.middleName, person?.lastName].filter(Boolean).join(" ");

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  return new Date(value).toLocaleDateString();
};

const statusColor = (status: TaskStatus) => {
  if (status === "Completed") return "success";
  if (status === "In Progress") return "warning";
  return "default";
};

const TaskManager: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [viewMode, setViewMode] = useState<"card" | "table">(isMobile ? "card" : "table");
  const [isLoading, setIsLoading] = useState(true);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Task | null>(null);
  const [search, setSearch] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });

  useEffect(() => {
    setViewMode(isMobile ? "card" : "table");
  }, [isMobile]);

  const showSnackbar = useCallback((
    severity: "success" | "error" | "info",
    message: string
  ) => setSnackbar({ open: true, message, severity }), []);

  const loadTasks = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getTasks();
      setTasks(res || []);
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to load tasks.");
    } finally {
      setIsLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  function getAssigneeLabel(task: Task) {
    if (task.assigneeType === "Unassigned") return "Unassigned";
    if (!task.assignee) return task.assigneeType;
    return `${formatName(task.assignee)} (${task.assigneeType})`;
  }

  function getLeadLabel(task: Task) {
    if (!task.lead) return "-";
    const name = formatName(task.lead) || task.lead.email || "Lead";
    return task.lead.businessName ? `${name} - ${task.lead.businessName}` : name;
  }

  function getRelatedLabel(task: Task) {
    if (task.deal) return task.deal.loanName || "Deal";
    return getLeadLabel(task);
  }

  function getRolePath(basePath: "leads" | "deals") {
    if (location.pathname.startsWith("/co-owner")) return `/co-owner/${basePath}`;
    if (location.pathname.startsWith("/employee")) return `/employee/${basePath}`;
    return `/${basePath}`;
  }

  function openRelated(task: Task) {
    if (task.deal?.id) {
      navigate(getRolePath("deals"), {
        state: { openDealId: task.deal.id },
      });
      return;
    }

    if (task.lead?.id) {
      navigate(getRolePath("leads"), {
        state: { openLeadId: task.lead.id },
      });
    }
  }

  const filteredTasks = (() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return tasks;

    return tasks.filter((task) =>
      [
        task.title,
        task.description,
        task.category,
        task.status,
        getAssigneeLabel(task),
        getRelatedLabel(task),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch)
    );
  })();

  const openCreateDialog = () => {
    setEditingTask(null);
    setOpenDialog(true);
  };

  const openEditDialog = (task: Task) => {
    setEditingTask(task);
    setOpenDialog(true);
  };

  const closeTaskDialog = (action?: string) => {
    setOpenDialog(false);
    setEditingTask(null);
    if (action === "reload") {
      loadTasks();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setIsLoading(true);
    try {
      await deleteTask(deleteTarget.id);
      showSnackbar("success", "Task deleted successfully.");
      await loadTasks();
    } catch (error: any) {
      showSnackbar("error", error?.message || "Failed to delete task.");
    } finally {
      setIsLoading(false);
      setDeleteTarget(null);
    }
  };

  const renderTaskTable = () => (
    <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
      <Table sx={{ borderCollapse: "collapse" }}>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#f9fafb" }}>
            {["Title", "Related", "Category", "Status", "Follow Up", "Assigned To", "Actions"].map((label) => (
              <TableCell
                key={label}
                align={label === "Actions" ? "center" : "left"}
                sx={{ fontWeight: "bold", border: "1px solid #e5e7eb" }}
              >
                {label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredTasks.map((task) => (
            <TableRow key={task.id} hover>
              <TableCell sx={{ border: "1px solid #e5e7eb", minWidth: 240 }}>
                <Typography className="font-semibold text-gray-800">
                  {task.title}
                </Typography>
                {task.description && (
                  <Typography variant="body2" className="text-gray-500">
                    {task.description}
                  </Typography>
                )}
              </TableCell>
              <TableCell sx={{ border: "1px solid #e5e7eb", minWidth: 220 }}>
                <span className="inline-flex items-center gap-1 text-gray-700">
                  <BusinessIcon fontSize="small" />
                  {getRelatedLabel(task)}
                </span>
                {(task.lead?.id || task.deal?.id) && (
                  <Tooltip title="Open related">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => openRelated(task)}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
              <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                <Chip label={task.category} size="small" variant="outlined" />
              </TableCell>
              <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                <Chip
                  label={task.status}
                  size="small"
                  color={statusColor(task.status) as any}
                />
              </TableCell>
              <TableCell sx={{ border: "1px solid #e5e7eb", whiteSpace: "nowrap" }}>
                <span className="inline-flex items-center gap-1 text-gray-700">
                  <EventIcon fontSize="small" />
                  {formatDate(task.followUpDate)}
                </span>
              </TableCell>
              <TableCell sx={{ border: "1px solid #e5e7eb" }}>
                <span className="inline-flex items-center gap-1 text-gray-700">
                  <PersonOutlineIcon fontSize="small" />
                  {getAssigneeLabel(task)}
                </span>
              </TableCell>
              <TableCell
                align="center"
                sx={{ border: "1px solid #e5e7eb", whiteSpace: "nowrap" }}
              >
                <Tooltip title="Edit">
                  <IconButton
                    color="secondary"
                    size="small"
                    onClick={() => openEditDialog(task)}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {task.canDelete && (
                  <Tooltip title="Delete">
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => setDeleteTarget(task)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderTaskCards = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-4 pb-6">
      {filteredTasks.map((task) => (
        <Card key={task.id} className="rounded-2xl shadow border border-gray-200">
          <CardContent className="flex flex-col px-4 py-6 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center shadow-sm shrink-0">
                <AssignmentTurnedInIcon className="text-gray-400" />
              </div>
              <div className="min-w-0">
                <Typography variant="h6" className="font-semibold text-gray-900 break-words">
                  {task.title}
                </Typography>
                {task.description && (
                  <Typography variant="body2" className="text-gray-500 break-words">
                    {task.description}
                  </Typography>
                )}
              </div>
            </div>

            <div className="text-sm text-gray-700 space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-gray-600 font-medium">Related:</span>
                <span>{getRelatedLabel(task)}</span>
                {(task.lead?.id || task.deal?.id) && (
                  <Tooltip title="Open related">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => openRelated(task)}
                    >
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-gray-600 font-medium">Category:</span>
                <Chip label={task.category} size="small" variant="outlined" />
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-gray-600 font-medium">Status:</span>
                <Chip
                  label={task.status}
                  color={statusColor(task.status) as any}
                  size="small"
                />
              </div>
              <div className="flex items-center gap-2">
                <EventIcon fontSize="small" />
                <span>
                  <strong>Follow Up:</strong> {formatDate(task.followUpDate)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <PersonOutlineIcon fontSize="small" />
                <span>
                  <strong>Assigned To:</strong> {getAssigneeLabel(task)}
                </span>
              </div>
            </div>

            <div
              className={`grid gap-2 w-full mt-auto ${
                task.canDelete ? "grid-cols-2" : "grid-cols-1"
              }`}
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<EditIcon />}
                color="secondary"
                fullWidth
                sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                onClick={() => openEditDialog(task)}
              >
                Edit
              </Button>
              {task.canDelete && (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<DeleteIcon />}
                  color="error"
                  fullWidth
                  sx={{ borderRadius: 2, py: 0.5, fontWeight: 600 }}
                  onClick={() => setDeleteTarget(task)}
                >
                  Delete
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );

  return (
    <div>
      <div className="space-y-4">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3 py-2 px-4">
          <Paper
            component="form"
            className="flex items-center border border-gray-300 rounded-full px-2 flex-1"
            elevation={0}
            sx={{
              borderRadius: 2,
              py: 0.5,
              fontWeight: 600,
              minWidth: { xs: "100%", lg: 360 },
            }}
          >
            <SearchIcon className="text-gray-400 ml-2" />
            <InputBase
              sx={{ ml: 1, flex: 1 }}
              placeholder="Search task"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
            {search && (
              <IconButton size="small" onClick={() => setSearch("")} sx={{ p: 0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Paper>

          <div className="flex items-center justify-center lg:justify-start flex-wrap gap-3">
            <Button
              variant="contained"
              startIcon={<AddTaskIcon />}
              onClick={openCreateDialog}
              sx={{
                borderRadius: 2,
                py: 0.5,
                fontWeight: 600,
                fontSize: { xs: "0.875rem", md: "1rem" },
                minWidth: { xs: "100%", md: "auto" },
              }}
            >
              Add Task
            </Button>
            <div className="flex items-center gap-2 rounded-xl border border-gray-300 overflow-hidden">
              <IconButton
                onClick={() => setViewMode("card")}
                className={`!p-2 ${viewMode === "card" ? "bg-gray-100" : ""}`}
                size="small"
              >
                <ViewModuleIcon
                  className={viewMode === "card" ? "text-black" : "text-gray-500"}
                  fontSize="small"
                />
              </IconButton>
              <IconButton
                onClick={() => setViewMode("table")}
                className={`!p-2 ${viewMode === "table" ? "bg-gray-100" : ""}`}
                size="small"
              >
                <ViewListIcon
                  className={viewMode === "table" ? "text-black" : "text-gray-500"}
                  fontSize="small"
                />
              </IconButton>
            </div>
          </div>
        </div>

        {isLoading ? (
          <Loader />
        ) : filteredTasks.length === 0 ? (
          <Box className="flex flex-col items-center justify-center py-32 text-center text-gray-500 bg-white rounded-lg border border-gray-200 mx-4">
            <div className="flex items-center justify-center mb-4">
              <div className="animate-pulse">
                <SearchRoundedIcon className="text-gray-400" sx={{ fontSize: 48 }} />
              </div>
            </div>
            <Typography variant="h6" className="mt-4 text-gray-700">
              No Tasks Found
            </Typography>
            <Typography variant="body2" className="text-gray-500">
              You can try adjusting your search or add a new task to get started.
            </Typography>
          </Box>
        ) : viewMode === "card" ? (
          renderTaskCards()
        ) : (
          <div className="px-4">{renderTaskTable()}</div>
        )}
      </div>

      <TaskFormDialog
        open={openDialog}
        onClose={closeTaskDialog}
        editTask={editingTask}
      />

      <ConfirmDeleteDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        itemName={deleteTarget?.title || ""}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TaskManager;
